import { Grid2, Stack } from '@mui/material';
import { capitalCase } from 'change-case';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Common } from '@components/Admin';
import IconButton from '@components/IconButton';
import ViewTab from '@components/ViewTab';

import { ORG_MANAGE_DETAILS_NOTES } from '@constants/routes';

import { usePageContext } from '@contexts/page-context';

import { useIsMobile } from '@hooks/useIsMobile';
import { useNavigate } from '@hooks/useNavigate';

import { formatDate } from '@utils/format';

import {
  useGetAccountRecord,
  useGetAccountUsers,
} from 'generated/account.generated';
import { AccountType } from 'generated/types';

import { UsersList } from '../../Users/List';
import { ChildAccounts } from '../Organization/ChildAccounts';

const messages = defineMessages({
  type: {
    id: 'admin.accounts.type',
    defaultMessage: 'Type',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created',
  },
  users: {
    id: 'admin.accounts.users',
    defaultMessage: 'Users',
  },
  accounts: {
    id: 'admin.accounts.patientAccounts',
    defaultMessage: 'Patient Accounts',
  },
});

interface AccountDrilldownProps {
  account_id: string;
}

const AccountDrilldown = ({
  account_id,
}: React.PropsWithChildren<AccountDrilldownProps>) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const { navigateRoute } = useNavigate();

  const tabs = [
    { id: 'users', value: formatMessage(messages.users) },
    { id: 'accounts', value: formatMessage(messages.accounts) },
  ];
  const [activeTab, setActiveTab] = React.useState(tabs[0]);

  React.useEffect(() => {
    setActiveTab(tabs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  const { data: accountData, loading: accountLoading } = useGetAccountRecord({
    variables: { input: { account_id } },
    skip: !account_id,
  });
  const { data: usersData, loading: usersLoading } = useGetAccountUsers({
    variables: {
      input: {
        account_id,
      },
    },
    skip: !account_id,
  });
  const account = accountData?.getAccountRecord;
  const users = usersData?.getAccountRecord.users;
  const isOrganization = account?.account_type === AccountType.organization;

  const { setTitle } = usePageContext();

  React.useEffect(() => {
    if (account?.account_name) {
      const { account_name } = account;
      const newTitle = account_name;
      setTitle(newTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.account_name]);

  const data: Common.Card.IMetadata.Metadata[] = [
    {
      title: formatMessage(messages.type),
      value: capitalCase(account?.account_type ?? '-'),
    },
    {
      title: formatMessage(messages.createdAt),
      value: formatDate({
        date: account?.created_at,
        formatter: 'datetime',
        fallback: '-',
      }),
    },
  ];

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 11, sm: 6 }}>
        <Stack gap={1}>
          <Common.Card.Metadata data={data} loading={accountLoading} />
        </Stack>
      </Grid2>
      <Grid2
        size={{ xs: 1, sm: 6 }}
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
        }}
      >
        {isOrganization && (
          <IconButton
            color={isMobile ? 'primary.main' : 'primary.contrastText'}
            iconType="fad"
            name="gear"
            onClick={() =>
              navigateRoute(ORG_MANAGE_DETAILS_NOTES, { account_id })
            }
            size={20}
          />
        )}
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <Stack gap={1}>
          {isOrganization && (
            <ViewTab
              changeTab={setActiveTab}
              selected={activeTab}
              tabArray={tabs}
            />
          )}

          {activeTab.id === 'users' && (
            <UsersList
              data={users}
              loading={usersLoading}
              account_id={account_id}
            />
          )}
          {activeTab.id === 'accounts' && (
            <ChildAccounts account_id={account_id} />
          )}
        </Stack>
      </Grid2>
    </Grid2>
  );
};

export default AccountDrilldown;
